import { Progress, UnstyledButton } from "@marketplace/ui";
import type { SVGProps } from "react";

type IntegrationStatusProgressProps = {
  percentage: number;
  onClick?: () => void;
};

export const IntegrationStatusProgress = ({
  percentage,
  onClick
}: IntegrationStatusProgressProps) => {
  const isComplete = percentage === 100;
  const progressColor = isComplete ? "#34C759" : "#FFCC00";

  return (
    <UnstyledButton
      onClick={onClick}
      className="w-full max-w-[134px] flex items-center gap-1 rounded-full px-[10px] py-[6px] border-solid border border-fjord-grey-400 hover:bg-gray-100"
    >
      <Progress
        value={percentage}
        color={progressColor}
        size="md"
        radius="xl"
        className="flex-1"
      />
      {isComplete && <CheckIcon className="text-[#34C759] shrink-0" />}
      {!isComplete && (
        <SpinnerIcon
          percentage={percentage}
          className="text-[#FFCC00] shrink-0"
        />
      )}
    </UnstyledButton>
  );
};

export const CheckIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <g clipPath="url(#clip0_40010316_54521)">
      <path
        d="M7.99992 1.33203C4.31992 1.33203 1.33325 4.3187 1.33325 7.9987C1.33325 11.6787 4.31992 14.6654 7.99992 14.6654C11.6799 14.6654 14.6666 11.6787 14.6666 7.9987C14.6666 4.3187 11.6799 1.33203 7.99992 1.33203ZM7.99992 13.332C5.05992 13.332 2.66659 10.9387 2.66659 7.9987C2.66659 5.0587 5.05992 2.66536 7.99992 2.66536C10.9399 2.66536 13.3333 5.0587 13.3333 7.9987C13.3333 10.9387 10.9399 13.332 7.99992 13.332ZM10.5866 5.52536L6.66658 9.44536L5.41325 8.19203C5.15325 7.93203 4.73325 7.93203 4.47325 8.19203C4.21325 8.45203 4.21325 8.87203 4.47325 9.13203L6.19992 10.8587C6.45992 11.1187 6.87992 11.1187 7.13992 10.8587L11.5333 6.46536C11.7933 6.20536 11.7933 5.78536 11.5333 5.52536C11.2733 5.26536 10.8466 5.26536 10.5866 5.52536V5.52536Z"
        fill="#34C759"
      />
    </g>
    <defs>
      <clipPath id="clip0_40010316_54521">
        <rect width={16} height={16} fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export interface SpinnerIconProps extends SVGProps<SVGSVGElement> {
  percentage?: number;
}

export const SpinnerIcon = ({ percentage, ...props }: SpinnerIconProps) => {
  const filledCount =
    percentage !== undefined
      ? Math.min(8, Math.max(0, Math.round((percentage / 100) * 8)))
      : 1;

  const circles = [
    { cx: 8.04633, cy: 1.64991, transform: "rotate(177.742 8.04633 1.64991)" }, // 12:00
    { cx: 12.8339, cy: 3.41272, transform: "rotate(-137.258 12.8339 3.41272)" }, // 1:30
    { cx: 14.9706, cy: 8.04835, transform: "rotate(177.742 14.9706 8.04835)" }, // 3:00
    { cx: 13.2052, cy: 12.8359, transform: "rotate(-47.2577 13.2052 12.8359)" }, // 4:30
    { cx: 8.57172, cy: 14.9702, transform: "rotate(177.742 8.57172 14.9702)" }, // 6:00
    { cx: 3.7846, cy: 13.2057, transform: "rotate(-137.258 3.7846 13.2057)" }, // 7:30
    { cx: 1.64789, cy: 8.57179, transform: "rotate(177.742 1.64789 8.57179)" }, // 9:00
    { cx: 3.41296, cy: 3.78509, transform: "rotate(-47.2577 3.41296 3.78509)" } // 10:30
  ];

  return (
    <svg
      width={17}
      height={17}
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {circles.map((circle, index) => (
        <circle
          key={index}
          cx={circle.cx}
          cy={circle.cy}
          r={1.33333}
          transform={circle.transform}
          fill={index < filledCount ? "#FFCC00" : "#F6F7F8"}
        />
      ))}
    </svg>
  );
};
